import { useEffect, useState } from "react";
import type { TugTeam } from "@/lib/types";
import { TEAM_TEXT, modeLabel } from "../utils";

interface Props {
  aName: string;
  bName: string;
  /** 승패를 가른 실제 점수 (동수 상위합계 · 1인 평균 등) */
  aScore: number;
  bScore: number;
  /** 전원 합계 (참고용) */
  aTaps: number;
  bTaps: number;
  aCnt: number;
  bCnt: number;
  /** 판정에 포함된 인원수 (top 모드) */
  compareN: number;
  scoreBasis?: string;
  judgeMode?: string;
  /** 서버가 주는 판정 기준 라벨 (top → "동수 상위합계") */
  modeLabelText?: string;
  winTeam?: string;
  winName?: string;
}

// 결과 밧줄 게이지.
// ★ 승패는 a_taps/b_taps 가 아니라 a_score : b_score 로 갈린다.
//   a_taps 가 더 큰데 상대가 이기는 경우가 정상이다 (인원이 3:2 면 상위 2명씩만 합산).
//   승패는 프런트에서 계산하지 않고 서버의 win_team 만 사용한다.
export default function TugGauge({
  aName,
  bName,
  aScore,
  bScore,
  aTaps,
  bTaps,
  aCnt,
  bCnt,
  compareN,
  scoreBasis,
  judgeMode,
  modeLabelText,
  winTeam,
  winName,
}: Props) {
  const total = (aScore || 0) + (bScore || 0);
  const draw = winTeam === "D";
  // 매듭 위치(왼쪽 %) : A 가 이길수록 왼쪽으로 간다.
  const target = draw ? 50 : total > 0 ? (bScore / total) * 100 : 50;
  const [pos, setPos] = useState(50);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    setPos(50);
    const t = window.setTimeout(() => setPos(target), 80);
    return () => window.clearTimeout(t);
  }, [target]);

  const winnerTeam: TugTeam = winTeam === "B" ? "B" : "A";
  const label = draw ? "무승부" : `${winName || (winnerTeam === "A" ? aName : bName)} 승리`;

  const basis = (scoreBasis ?? "").trim() || modeLabel(judgeMode, modeLabelText);
  // 전원 합계로 판정한 게 아니면, 합계로는 어떻게 보이는지 참고용으로 덧붙인다.
  const showFullSum = basis !== "전원 합계" && ((aTaps || 0) > 0 || (bTaps || 0) > 0);
  const tipText =
    judgeMode === "avg"
      ? "양 팀 인원이 달라 1인 평균으로 겨뤘습니다"
      : `인원이 ${aCnt}:${bCnt} 로 달라서, 공정하게 양 팀 상위 ${compareN}명씩만 합산했습니다`;

  return (
    <div className="rounded-2xl border border-background-200 bg-background-100 px-5 py-6">
      {/* 승자 */}
      <p className="mb-5 text-center font-heading text-xl font-extrabold text-foreground-950">
        {!draw && <span aria-hidden="true">🏆 </span>}
        {label}
      </p>

      <div className="flex items-center gap-3">
        {/* A팀 */}
        <div className="w-24 shrink-0 text-right">
          <p className={`font-heading text-sm font-bold ${TEAM_TEXT.A}`}>{aName}</p>
          <p className="font-heading text-2xl font-extrabold tabular-nums text-foreground-950">
            {aScore}
          </p>
        </div>

        {/* 밧줄 */}
        <div className="relative h-3 flex-1 rounded-full bg-background-300">
          <span
            className="tug-gauge-knot absolute -top-2.5 -ml-4 flex h-8 w-8 items-center justify-center rounded-full bg-foreground-900 text-background-50"
            style={{ left: `${pos}%` }}
            aria-hidden="true"
          >
            <span className="h-2 w-2 rounded-full bg-background-50"></span>
          </span>
        </div>

        {/* B팀 */}
        <div className="w-24 shrink-0 text-left">
          <p className={`font-heading text-sm font-bold ${TEAM_TEXT.B}`}>{bName}</p>
          <p className="font-heading text-2xl font-extrabold tabular-nums text-foreground-950">
            {bScore}
          </p>
        </div>
      </div>

      {/* 판정 기준 — 반드시 표시 */}
      <p className="mt-3 text-center text-xs font-semibold text-foreground-600">
        {basis} 기준
      </p>

      {/* 전원 합계 참고값 + ⓘ 설명 */}
      {showFullSum && (
        <div className="mt-1.5 flex items-center justify-center gap-1.5 text-xs text-foreground-500">
          <span className="tabular-nums">
            전원 합계로는 {aTaps} : {bTaps}
          </span>
          <span className="relative inline-flex">
            <button
              type="button"
              onClick={() => setShowTip((v) => !v)}
              aria-label="판정 기준 설명"
              className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700"
            >
              <i className="ri-information-line text-sm" aria-hidden="true"></i>
            </button>
            {showTip && (
              <span className="absolute bottom-7 left-1/2 z-10 w-56 -translate-x-1/2 rounded-lg bg-foreground-950 px-3 py-2 text-[11px] font-normal leading-relaxed text-background-50 shadow-lg">
                {tipText}
              </span>
            )}
          </span>
        </div>
      )}

      <p className="mt-2 text-center text-xs text-foreground-400">
        승부 기준 : {modeLabel(judgeMode, modeLabelText)}
        {judgeMode !== "avg" && basis !== "전원 합계"
          ? " · 인원이 적은 쪽에 맞춰 상위 인원만 합산합니다"
          : ""}
      </p>
    </div>
  );
}